import { Box, Card, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Social_Tag, Social_Tag_Type_Enum } from '@kheops/graphql';
import { round } from '@kheops/utils';
import i18n from '../i18n';
import TrendingImage from '../assets/images/3d-icons/trending.png';
import FlameImage from '../assets/images/3d-icons/flame.png';
import GoldNuggetsImage from '../assets/images/3d-icons/gold-nuggets.png';
import SproutImage from '../assets/images/3d-icons/sprout.png';
import VerifiedShieldImage from '../assets/images/3d-icons/verified-shield.png';
import MoneyBagImage from '../assets/images/3d-icons/money-bag.png';
import LightningImage from '../assets/images/3d-icons/lightning.png';
import { TAG_LEVEL_ACCENT_COLORS, TAG_LEVEL_CONTAINER_COLORS } from './state';

type SocialTagEntity = 'SUPPLIER' | 'BASE_UNIT';
type DisplaySocialTag = Pick<Social_Tag, 'type' | 'priority' | 'level' | 'values'>;
type TextFormat = 'integer' | 'percent';

interface SocialTagTextInfo {
  value: string;
  format: TextFormat;
}

type SocialTagDisplayInfoMap = {[entity in SocialTagEntity]?: SocialTagTextInfo};

interface SocialTagDisplayInfo {
  icon: string;
  textInfo: SocialTagDisplayInfoMap;
}

export interface SocialTagCardProps {
  tag: DisplaySocialTag;
  entity: SocialTagEntity;
}

// values name used here are the columns from Social Proof BigQuery KPIs table
const SocialTagDisplayInfoMap: {[type in Social_Tag_Type_Enum]?: SocialTagDisplayInfo} = {
  [Social_Tag_Type_Enum.Trend]: {
    icon: TrendingImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_gmv_growth_rate_60d',
        format: 'percent',
      },
      BASE_UNIT: {
        value: 'base_unit_gmv_growth_rate_60d',
        format: 'percent',
      },
    },
  },
  [Social_Tag_Type_Enum.Popular]: {
    icon: FlameImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_active_contracts_12m',
        format: 'integer',
      },
      BASE_UNIT: {
        value: 'base_unit_unique_contracts_12m',
        format: 'integer',
      },
    },
  },
  [Social_Tag_Type_Enum.HiddenGem]: {
    icon: GoldNuggetsImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_reorder_rate_3m',
        format: 'percent',
      },
      BASE_UNIT: {
        value: 'base_unit_reorder_rate_3m',
        format: 'percent',
      },
    },
  },
  [Social_Tag_Type_Enum.NewArrival]: {
    icon: SproutImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_age_days',
        format: 'integer',
      },
      BASE_UNIT: {
        value: 'base_unit_age_days',
        format: 'integer',
      },
    },
  },
  [Social_Tag_Type_Enum.GuaranteedSales]: {
    icon: VerifiedShieldImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_reorder_rate_3m',
        format: 'percent',
      },
      BASE_UNIT: {
        value: 'base_unit_reorder_rate_3m',
        format: 'percent',
      },
    },
  },
  [Social_Tag_Type_Enum.HighMargin]: {
    icon: MoneyBagImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_avg_margin_rate',
        format: 'percent',
      },
      BASE_UNIT: {
        value: 'base_unit_avg_margin_rate',
        format: 'percent',
      },
    },
  },
  [Social_Tag_Type_Enum.HighlyResponsive]: {
    icon: LightningImage,
    textInfo: {
      SUPPLIER: {
        value: 'supplier_avg_validation_hours',
        format: 'integer',
      },
    },
  },
};

export default function SocialTagCard({ tag, entity }: SocialTagCardProps): React.JSX.Element {
  const { t } = useTranslation(['social-proof']);
  const percentFormatter = new Intl.NumberFormat(i18n.resolvedLanguage, { style: 'percent', maximumFractionDigits: 2 });

  const getMainTextValue = (displayTag: DisplaySocialTag): string | number => {
    const value = displayTag.values![SocialTagDisplayInfoMap[displayTag.type]!.textInfo[entity]!.value];

    if (SocialTagDisplayInfoMap[displayTag.type]!.textInfo[entity]!.format === 'percent') {
      return percentFormatter.format(value);
    }

    return round(value, 0);
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        p: 1.5,
        pl: 0,
        '&.MuiPaper-root': {
          backgroundColor: `${TAG_LEVEL_CONTAINER_COLORS[tag.level - 1]}.main`,
        },
        display: 'flex',
        flexDirection: {
          xs: 'column-reverse',
          md: 'row',
        },
        gap: {
          xs: 1,
          md: 1.5,
        },
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <img src={SocialTagDisplayInfoMap[tag.type]!.icon} height="64" width="64" alt="" />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="bodySmall" sx={{ mb: 0.5 }}>
            {t(`social-proof:${tag.type.toLowerCase()}.${entity.toLowerCase()}.main_text`)}
          </Typography>
          <Typography variant="titleMedium">
            {t(`social-proof:${tag.type.toLowerCase()}.${entity.toLowerCase()}.main_text_value`, { value: getMainTextValue(tag) })}
          </Typography>
          <Typography variant="labelSmall" sx={{ color: `${TAG_LEVEL_ACCENT_COLORS[tag.level - 1]}.main` }}>
            {t(`social-proof:${tag.type.toLowerCase()}.${entity.toLowerCase()}.sub_text`)}
          </Typography>
        </Box>
      </Box>
      <Chip
        label={t(`social-proof:${tag.type.toLowerCase()}.title`)}
        variant="accentTag"
        color={TAG_LEVEL_ACCENT_COLORS[tag.level - 1]}
        sx={{
          ml: 1.5,
          top: 12,
          right: 12,
        }}
      />
    </Card>
  );
}
